<template>
  <div class="approvals-page">
    <div class="toolbar">
      <div class="title-block">
        <h1>Согласования</h1>
        <span class="counter">{{ totalItems }} текущих</span>
      </div>

      <div class="controls">
        <div class="scope-switch" aria-label="Статус согласований">
          <button class="scope-button" :class="{ active: isActiveScope }" type="button" @click="setActiveScope">Активные</button>
          <button class="scope-button" :class="{ active: isAllScope }" type="button" @click="setAllScope">Все</button>
        </div>
        <input class="input" :value="trip" type="text" placeholder="Рейс" @input="onTrip" />
        <input class="input" :value="responsible" type="text" placeholder="Текущий ответственный" @input="onResponsible" />
        <input class="input date-input" :value="dateFrom" type="date" title="Дата согласования с" @input="onDateFrom" />
        <input class="input date-input" :value="dateTo" type="date" title="Дата согласования по" @input="onDateTo" />
        <button class="btn" type="button" @click="resetFilters">Сбросить</button>
      </div>
    </div>

    <div v-if="hasError" class="state-block error">
      Не удалось загрузить заявки на согласование.
    </div>

    <div v-else class="table-wrap">
      <my-loader v-if="loading && items.length === 0" />

      <table v-else-if="items.length" class="approvals-table">
        <thead>
          <tr>
            <th>Номер</th>
            <th>Погрузка</th>
            <th>Откуда</th>
            <th>Куда</th>
            <th>Платформа</th>
            <th>Текущий тариф</th>
            <th>Запрошено</th>
            <th>Оплата</th>
            <th>Текущий ответственный</th>
            <th>Причина</th>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.uid || item.distr_id">
            <td>
              <router-link v-if="canOpenTrip(item)" class="trip-link" :to="`/trips/${item.trip_id}`">
                {{ item.distr_id || item.trip_id }}
              </router-link>
              <span v-else :title="item.trip_id || item.uid">{{ item.distr_id || item.trip_id || '—' }}</span>
            </td>
            <td>{{ formatDate(item.route?.loading_date) }}</td>
            <td :title="item.route?.from">{{ item.route?.from || '—' }}</td>
            <td :title="item.route?.to">{{ item.route?.to || '—' }}</td>
            <td>{{ item.display_name || '—' }}</td>
            <td>{{ tariffText(item.current_tariff) }}</td>
            <td><span class="requested">{{ requestedText(item.requested) }}</span></td>
            <td>{{ item.requested?.payment_term || '—' }}</td>
            <td>{{ currentResponsibleText(item.current_responsible) }}</td>
            <td class="reason" :title="reasonText(item.comments)">{{ reasonText(item.comments) }}</td>
            <td>
              <div class="actions">
                <span v-if="item.approval_state === 'confirmed'" class="confirmed">Согласовано</span>
                <button
                  v-else
                  class="action-btn accept"
                  type="button"
                  :disabled="isDecisionLoading(item)"
                  @click="decide(item, 'confirm')"
                >
                  Принять
                </button>
                <button
                  v-if="item.approval_state !== 'confirmed'"
                  class="action-btn deny"
                  type="button"
                  :disabled="isDecisionLoading(item)"
                  @click="decide(item, 'deny')"
                >
                  Отклонить
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else class="state-block">
        Текущих заявок на согласование нет.
      </div>
    </div>

    <my-pagination
      v-if="totalPages > 1"
      :currentPage="currentPage"
      :totalPages="totalPages"
      @pageChanged="pageChanged"
    />
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return new Intl.NumberFormat('ru-RU').format(number);
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export default {
  name: 'DistributionApprovalsPage',
  setup() {
    const store = useStore();
    const hasError = ref(false);
    let pollTimer = null;
    let filterTimer = null;
    const state = computed(() => store.state.distributionApprovals);

    const fetchData = () => {
      store.dispatch('distributionApprovals/getApprovals')
        .then(() => { hasError.value = false; })
        .catch(() => { hasError.value = true; });
    };

    const setActiveScope = () => store.dispatch('distributionApprovals/setApprovalState', 'pending');
    const setAllScope = () => store.dispatch('distributionApprovals/setApprovalState', 'all');

    const debounceFilter = (action, value) => {
      if (filterTimer) clearTimeout(filterTimer);
      filterTimer = setTimeout(() => store.dispatch(action, value), 250);
    };
    const onTrip = (event) => debounceFilter('distributionApprovals/setTrip', event.target.value);
    const onResponsible = (event) => debounceFilter('distributionApprovals/setResponsible', event.target.value);
    const onDateFrom = (event) => debounceFilter('distributionApprovals/setDateFrom', event.target.value);
    const onDateTo = (event) => debounceFilter('distributionApprovals/setDateTo', event.target.value);
    const resetFilters = () => store.dispatch('distributionApprovals/resetFilters');

    const pageChanged = (page) => {
      store.dispatch('distributionApprovals/setPage', page);
    };

    const getUid = (item) => item?.uid || null;
    const canOpenTrip = (item) => /^\d+$/.test(String(item?.trip_id || ''));
    const isDecisionLoading = (item) => Boolean(state.value.decisionLoading?.[getUid(item)]);
    const decide = (item, decision) => {
      const uid = getUid(item);
      if (!uid || isDecisionLoading(item)) return;
      store.dispatch('distributionApprovals/decideApproval', { uid, decision })
        .then(() => { hasError.value = false; })
        .catch(() => { hasError.value = true; });
    };

    const tariffText = (tariff) => {
      if (!tariff) return '—';
      return `${formatMoney(tariff.cost_without_nds)} / ${formatMoney(tariff.cost_with_nds)}`;
    };

    const requestedText = (requested) => {
      if (!requested) return '—';
      const cost = `${formatMoney(requested.cost_without_nds)} / ${formatMoney(requested.cost_with_nds)}`;
      return requested.new_date ? `${cost}, дата` : cost;
    };

    const currentResponsibleText = (responsible) => {
      if (!responsible) return '—';
      if (typeof responsible === 'string') return responsible;
      return responsible.name || responsible.username || responsible.id || '—';
    };

    const reasonText = (comments) => {
      if (!comments) return '—';
      return [comments.who_carrier, comments.why_tariff, comments.prev_terms].filter(Boolean).join(' · ') || '—';
    };

    onMounted(() => {
      fetchData();
      pollTimer = setInterval(fetchData, 10000);
    });

    onBeforeUnmount(() => {
      if (pollTimer) clearInterval(pollTimer);
      if (filterTimer) clearTimeout(filterTimer);
    });

    return {
      hasError,
      fetchData,
      setActiveScope,
      setAllScope,
      onTrip,
      onResponsible,
      onDateFrom,
      onDateTo,
      resetFilters,
      pageChanged,
      canOpenTrip,
      decide,
      isDecisionLoading,
      formatDate,
      tariffText,
      requestedText,
      currentResponsibleText,
      reasonText,
      items: computed(() => state.value.items || []),
      loading: computed(() => Boolean(state.value.loading)),
      isActiveScope: computed(() => state.value.approvalState === 'pending'),
      isAllScope: computed(() => state.value.approvalState === 'all'),
      trip: computed(() => state.value.trip || ''),
      responsible: computed(() => state.value.responsible || ''),
      dateFrom: computed(() => state.value.dateFrom || ''),
      dateTo: computed(() => state.value.dateTo || ''),
      currentPage: computed(() => state.value.currentPage || 1),
      totalPages: computed(() => state.value.totalPages || 1),
      totalItems: computed(() => state.value.totalItems || 0),
    };
  },
};
</script>

<style scoped>
.approvals-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 14px 16px;
  color: #333;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.title-block {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

h1 {
  margin: 0;
  font-size: 22px;
  line-height: 1.2;
}

.counter {
  color: #667085;
  font-size: 13px;
}

.controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.input,
.btn {
  height: 32px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 13px;
}

.scope-switch {
  display: flex;
  overflow: hidden;
  border: 1px solid #d7dce2;
  border-radius: 4px;
}

.scope-button {
  height: 32px;
  padding: 0 10px;
  border: 0;
  border-right: 1px solid #d7dce2;
  background: #fff;
  color: #667085;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: inherit;
}

.scope-button:last-child {
  border-right: 0;
}

.scope-button.active {
  background: #e7f1ff;
  color: #0b63ce;
}

.input {
  width: 180px;
  padding: 0 8px;
}

.date-input {
  width: 148px;
}

.btn {
  padding: 0 12px;
  cursor: pointer;
}

.btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.table-wrap {
  flex: 1;
  overflow: auto;
}

.approvals-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
}

.approvals-table th,
.approvals-table td {
  height: 32px;
  padding: 6px 8px;
  border-bottom: 1px solid #edf0f3;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.approvals-table th {
  background: #f7f8fa;
  color: #444;
  font-weight: 700;
}

.approvals-table th:nth-child(1),
.approvals-table td:nth-child(1) { width: 96px; }
.approvals-table th:nth-child(2),
.approvals-table td:nth-child(2) { width: 120px; }
.approvals-table th:nth-child(3),
.approvals-table td:nth-child(3),
.approvals-table th:nth-child(4),
.approvals-table td:nth-child(4) { width: 150px; }
.approvals-table th:nth-child(5),
.approvals-table td:nth-child(5) { width: 130px; }
.approvals-table th:nth-child(6),
.approvals-table td:nth-child(6),
.approvals-table th:nth-child(7),
.approvals-table td:nth-child(7) { width: 150px; }
.approvals-table th:nth-child(8),
.approvals-table td:nth-child(8) { width: 110px; }
.approvals-table th:nth-child(9),
.approvals-table td:nth-child(9) { width: 120px; }
.approvals-table th:nth-child(11),
.approvals-table td:nth-child(11) { width: 150px; }

.approvals-table tbody tr:hover {
  background: #f3f6fb;
}

.trip-link {
  color: #0b63ce;
  font-weight: 600;
  text-decoration: none;
}

.trip-link:hover {
  text-decoration: underline;
}

.requested {
  font-weight: 600;
}

.actions {
  display: flex;
  gap: 6px;
}

.action-btn {
  height: 28px;
  padding: 0 8px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 12px;
  cursor: pointer;
}

.action-btn:disabled {
  cursor: default;
  opacity: 0.6;
}

.action-btn.accept {
  border-color: #8fcaa6;
  color: #0b6b44;
}

.action-btn.deny {
  border-color: #e6a2a2;
  color: #9f2f2f;
}

.confirmed {
  color: #0b6b44;
  font-weight: 600;
}

.reason {
  color: #4b5563;
}

.state-block {
  padding: 32px;
  color: #667085;
  font-size: 14px;
}

.state-block.error {
  color: #dc3545;
}

@media screen and (max-width: 769px) {
  .toolbar {
    flex-direction: column;
    align-items: flex-start;
  }
  .controls {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
